import { useEffect, useState } from 'react';

/**
 * Listens to a microphone input (the Focusrite's mic channel) and reports
 * whether someone is speaking. Analysis only: the signal is never played back —
 * the mics reach the speakers through the interface's Direct Monitor.
 */
export function useVoiceDetection(enabled: boolean, deviceId: string | null, threshold: number): { speaking: boolean; level: number } {
  const [speaking, setSpeaking] = useState(false);
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!enabled || !deviceId) {
      setSpeaking(false);
      return;
    }
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let raf = 0;
    let cancelled = false;
    let lastLoud = 0;
    let loudSince = 0;

    void (async () => {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId }, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      if (cancelled) return stream.getTracks().forEach((t) => t.stop());
      ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      let frame = 0;
      const loop = () => {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += v * v;
        const rms = Math.sqrt(sum / buf.length);
        const now = performance.now();
        if (rms > threshold) {
          if (!loudSince) loudSince = now;
          lastLoud = now;
        } else if (now - lastLoud > 200) {
          loudSince = 0;
        }
        // Speaking after 150 ms above threshold; released 1.5 s after the voice stops.
        setSpeaking((was) => (was ? now - lastLoud < 1500 : loudSince > 0 && now - loudSince > 150));
        if (++frame % 6 === 0) setLevel(rms);
        raf = requestAnimationFrame(loop);
      };
      loop();
    })().catch(() => setSpeaking(false));

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      void ctx?.close();
    };
  }, [enabled, deviceId, threshold]);

  return { speaking, level };
}

/** Keeps the Mac's screen awake while the party runs. */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    const acquire = () => {
      void navigator.wakeLock
        .request('screen')
        .then((l) => (lock = l))
        .catch(() => undefined);
    };
    acquire();
    const onVisible = () => document.visibilityState === 'visible' && acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release();
    };
  }, [active]);
}

export function useAudioInputs(): MediaDeviceInfo[] {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  useEffect(() => {
    const refresh = () => void navigator.mediaDevices?.enumerateDevices().then((d) => setDevices(d.filter((x) => x.kind === 'audioinput')));
    refresh();
    navigator.mediaDevices?.addEventListener('devicechange', refresh);
    return () => navigator.mediaDevices?.removeEventListener('devicechange', refresh);
  }, []);
  return devices;
}
